import type { MessageDescriptor } from "@lingui/core";
import { Show } from "solid-js";
import { COMPACT_THRESHOLD, formatCompactNumber } from "@fleet/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@fleet/ui";
import { useI18n } from "@/providers/i18n-provider";
import { cn } from "@time-tracker/shared";

interface CounterBadgeProps {
  count: number | undefined;
  class?: string;
  /**
   * Counted noun, as a MACRO (`` msg`entries` ``) — never a string
   * literal, because the Lingui extractor only sees macros. Without `label`, no
   * noun is invented: the accessible label is the number alone.
   *
   * It used to be `label?: string` with the HARDCODED `?? "tasks"` fallback: the only
   * consumer (`sidebar-nav-item.tsx`) never passed `label`, so that
   * fallback —in Spanish and using the template's vocabulary— accounted for
   * 100% of renders in all 11 languages. This repo has no single natural noun
   * to replace it (the sidebar `count` is a generic stub shared by heterogeneous
   * items, not a list of one entity like "tasks" in the source clone), so none
   * is invented.
   */
  label?: MessageDescriptor;
  tooltipDisabled?: boolean;
  showZero?: boolean;
}

export function CounterBadge(props: CounterBadgeProps) {
  const { locale, t } = useI18n();

  const isCompact = (): boolean => props.count !== undefined && props.count >= COMPACT_THRESHOLD;

  const formattedCount = (): string =>
    props.count === undefined ? "" : formatCompactNumber(props.count, locale());

  // Localized description for accessibility and tooltips.
  //
  // `props.count` / `props.label` are ALWAYS read through property access, never
  // destructured: destructuring breaks Solid reactivity.
  const fullLabel = (): string => {
    if (props.count === undefined) {
      return "";
    }
    const formatted = props.count.toLocaleString(locale());
    return props.label === undefined ? formatted : `${formatted} ${t(props.label)}`;
  };

  return (
    <Show
      when={props.count !== undefined && (props.count > 0 || (props.showZero && props.count === 0))}
    >
      <Show
        when={isCompact() && !props.tooltipDisabled}
        fallback={
          <span
            class={cn(
              "inline-flex items-center justify-center px-1.5 py-0.5 rounded-full bg-muted-foreground/10 text-muted-foreground transition-all text-[10px] font-bold select-none animate-fade-in",
              props.class,
            )}
            aria-label={fullLabel()}
          >
            {props.count}
          </span>
        }
      >
        <Tooltip openDelay={350} closeDelay={100}>
          <TooltipTrigger class="focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent rounded-full select-none cursor-help">
            <span
              class={cn(
                "inline-flex items-center justify-center px-1.5 py-0.5 rounded-full bg-muted-foreground/10 text-muted-foreground hover:bg-accent/15 hover:text-accent transition-all text-[10px] font-bold animate-fade-in",
                props.class,
              )}
              aria-label={fullLabel()}
            >
              {formattedCount()}
            </span>
          </TooltipTrigger>
          <TooltipContent class="max-w-[200px] text-center font-semibold bg-background text-foreground border border-border/40 shadow-lg px-2.5 py-1.5 rounded-lg select-none">
            {fullLabel()}
          </TooltipContent>
        </Tooltip>
      </Show>
    </Show>
  );
}
